<template>
  <div class="info-pane">
    <div class="info-box info-box-user">
      <h3 class="header">{{ $t('funnel.sessionReport.userInfo.title') }}</h3>
      <button class="btn__toggle-slide"
        @click="showUserInfo = !showUserInfo">
        <i class="icon icon--toggle-slide20"
          :style="{ transform: `rotate(${showUserInfo ? 0 : 180}deg` }"/>
      </button>
      <table class="info" v-show="showUserInfo">
        <tr>
          <th>{{ $t('funnel.sessionReport.userInfo.sessionId') }}</th>
          <td colspan="2">{{ streamMeta.ssid }}</td>
        </tr>
        <tr>
          <th>{{ $t('funnel.sessionReport.userInfo.location') }}</th>
          <td class="info-icon">
            <country-flag v-if="streamMeta.location"
              class="country-flag"
              :countryCode="streamMeta.location"/>
          </td>
          <td class="info-text">
            {{ countryName[streamMeta.location] || streamMeta.location }}
          </td>
        </tr>
        <tr>
          <th>{{ $t('funnel.sessionReport.userInfo.device') }}</th>
          <td class="info-icon">
            <span class="icon icon--device" :class="`icon--${streamMeta.d}`"></span>
          </td>
          <td class="info-text">
            {{ streamMeta.d | upperFirst }}
          </td>
        </tr>
        <tr>
          <th>{{ $t('funnel.sessionReport.userInfo.resolution') }}</th>
          <td colspan="2">
            <template v-if="streamMeta.ss_w">
              {{ streamMeta.ss_w }} x {{ streamMeta.ss_h }}
            </template>
          </td>
        </tr>
        <tr>
          <th>{{ $t('funnel.sessionReport.userInfo.browser') }}</th>
          <td class="info-icon">
            <span class="icon icon--browser"
              :class="streamMeta.br | browserIconName"></span>
          </td>
          <td class="info-text">
            {{ streamMeta.br }}
          </td>
        </tr>
        <tr>
          <th>{{ $t('funnel.sessionReport.userInfo.os') }}</th>
          <td class="info-icon">
            <span class="icon icon--os"
              :class="streamMeta.os | osIconName"></span>
          </td>
          <td class="info-text">
            {{ streamMeta.os }}
          </td>
        </tr>
      </table>
    </div>
    <div class="info-box info-box-activity">
      <h3 class="header">{{ $t('funnel.sessionReport.activityInfo.title') }}</h3>
      <button class="btn__toggle-slide"
        @click="showActivityInfo = !showActivityInfo">
        <i class="icon icon--toggle-slide20"
          :style="{ transform: `rotate(${showActivityInfo ? 0 : 180}deg` }"/>
      </button>

      <table class="info" v-show="showActivityInfo">
        <tr>
          <th>{{ $t('funnel.sessionReport.activityInfo.visitDuration') }}</th>
          <td>{{ sessionData ? sessionData.totalDurationStr : '' }}</td>
        </tr>
        <tr>
          <th>{{ $t('funnel.sessionReport.activityInfo.eventCount') }}</th>
          <td>{{ nodeItems.length }}</td>
        </tr>
        <tr>
          <th>{{ $t('funnel.sessionReport.activityInfo.movementDistance') }}</th>
          <td>{{ streamMeta.distance || 0 }}px</td>
        </tr>
      </table>
    </div>
    <div class="info-box info-box-history">
      <h3 class="header">{{ $t('funnel.sessionReport.activityHistory.title') }}</h3>
      <p v-if="streamStartDate" class="start-date">
        {{ $t('funnel.sessionReport.activityHistory.startDate') }} : {{ streamStartDate }}
      </p>

      <table class="history" ref="historyTable">
        <colgroup>
          <col width="38">
          <col width="82">
          <col width="90">
          <col width="*">
        </colgroup>
        <thead>
          <tr>
            <th class="order">{{ $t('funnel.sessionReport.activityHistory.sequence') }}</th>
            <th class="time">{{ $t('funnel.sessionReport.activityHistory.time') }}</th>
            <th class="duration">{{ $t('funnel.sessionReport.activityHistory.duration') }}</th>
            <th class="action">{{ $t('funnel.sessionReport.activityHistory.action') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) of nodeItems" :key="index"
            :class="{
              active: activeIndex === item.index,
              hover: hoverIndex === item.index
            }"
            @mouseenter="$emit('update:hoverIndex', item.index)"
            @mouseleave="$emit('update:hoverIndex', null)"
            @click="$emit('setActiveItem', item)">
            <td class="order">
              <div class="order-box">
                <span>{{ index + 1 }}</span>
                <div v-if="activeIndex === item.index"
                  class="active-mask"
                  :style="{ width: tableWidth + 'px' }"></div>
              </div>
            </td>
            <td class="time">{{ item.nodeData.date | dateFormatTimezone(userData.timezoneName, 'HH:mm:ss.SSS') }}</td>
            <td class="duration">{{ formatTime(item.duration, 3, false) }}</td>
            <td class="action">
              <template v-if="item.type === 'hoverClick'">
                Hover -> Click
              </template>
              <template v-else>
                {{ NODE_TYPE[item.type].name }}
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { parse } from 'date-fns'
import countryName from '@/data/country-name'
import CountryFlag from '@/components/ui/CountryFlag'
import { utcToLocalTimezone, dateFormatTimezone } from '@/libs/utils'
import { mapState } from 'vuex'

export default {
  name: 'FunnelSessionInfo',
  components: {
    CountryFlag
  },
  props: [
    'NODE_TYPE',
    'sessionData',
    'streamMeta',
    'nodeItems',
    'hoverIndex',
    'activeIndex',
    'formatTime',
    'timezoneName'
  ],
  data () {
    return {
      tableWidth: 0,
      showUserInfo: true,
      showActivityInfo: true
    }
  },
  computed: {
    ...mapState([
      'userData'
    ]),
    streamStartDate () {
      if (this.streamMeta.ct) {
        return dateFormatTimezone(parse(this.streamMeta.ct), this.userData.timezoneName, 'YYYY.MM.DD')
      }
      return null
    }
  },
  created () {
    this.countryName = countryName
  },
  mounted () {
    const el = this.$refs.historyTable

    if (el) {
      this.tableWidth = el.offsetWidth
    }
  }
}
</script>

<style lang="scss" scoped>
.info-pane {
  width: 410px;
  padding: 20px 5px 20px 20px;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  float: left;
  height: 100vh;
  // background-color: blue;

  .info-box {
    position: relative;
    box-sizing: border-box;
    width: 370px;
    padding: 25px;
    margin-bottom: 10px;
    border-radius: 14px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
    background-color: #fff;

    .btn__toggle-slide {
      &:hover {
        @include hoverStyle;
      }
      width: 32px;
      height: 32px;
      padding: 6px;
      background-position: center;
      border-radius: 50%;
      position: absolute;
      top: 17px;
      right: 17px;

      .icon {
        pointer-events: none;
      }
    }

    h3 {
      font-size: 15px;
      font-weight: bold;
      color: #2f2f2f;
    }

    &.info-box-user {
      .info {
        width: 100%;
        margin-top: 18px;
        margin-bottom: -8px;
        font-size: 13px;

        th {
          width: 102px;
          height: 33px;
          text-align: left;
          color: #666;
        }
        td {
          height: 33px;
          color: #2f2f2f;

          &.info-icon {
            text-align: center;
            width: 26px;
          }
          &.info-text {
            padding-left: 7px;
          }
        }

        .country-flag {
          margin: -10px 0 -9px 0;
        }

        .icon--device,
        .icon--browser,
        .icon--os {
          vertical-align: middle;
          margin: -10px 0 -9px 0;
          width: 20px;
          height: 20px;
          background-size: cover;
        }
      }
    }

    &.info-box-activity {
      .info {
        width: 100%;
        margin-top: 28px;

        tr {
          border-top: 1px dashed #d7d7d7;

          &:first-of-type {
            border-top: 1px solid #bbb;
          }
        }

        th,
        td {
          height: 39px;
          width: 50%;
          font-size: 13px;
        }
        th {
          text-align: left;
          padding-left: 4px;
          color: #666;
        }
        td {
          text-align: right;
          padding-right: 4px;
          color: #2f2f2f;
        }
      }
    }

    &.info-box-history {
      .start-date {
        position: absolute;
        top: 27px;
        right: 25px;
        color: #666;
      }

      .history {
        margin-top: 30px;
        width: 100%;
        text-align: center;

        thead tr th {
          padding-bottom: 8px;
          border-bottom: 1px solid #bbbbbb;
          font-size: 12px;
          color: #444;
          font-weight: normal;

          &.order {
            padding-left: 4px;
            text-align: left;
          }
        }

        tbody tr {
          height: 39px;
          border-bottom: 1px dashed #d7d7d7;

          &:last-of-type {
            border-bottom: 0;
          }

          &.hover {
            cursor: pointer;
            td {
              background-color: rgba(0, 0, 0, 0.06);
            }
          }

          &.active {
            cursor: default;
            &.hover {
              td {
                background-color: transparent;
              }
            }
            .active-mask {
              position: absolute;
              top: -1px;
              left: -10px;
              z-index: 1;
              pointer-events: none;
              height: 37px;
              padding: 0 8px;
              border-radius: 10px;
              box-shadow: 0 12px 10px -4px rgba(0, 0, 0, 0.15);
              border: solid 2px $brandColor;
            }
          }
        }

        td {
          font-size: 13px;
          color: #2f2f2f;

          &.order {
            position: relative;
            text-align: left;
            .order-box span {
              display: inline-block;
              margin-left: 4px;
              width: 20px;
              text-align: center;
            }
          }
        }
      }
    }
  }
}
</style>
